import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {
    Tabs,
    Card,
    Typography,
    Button,
    Spin,
    App,
    Row,
    Col,
    Steps,
    Space,
    Tag
} from 'antd';
import {authService, paymentMethodService} from '../../services/api';
import {usePageTitle} from '../../context/PageTitleContext';
import ErrorPage from "../ErrorPage/ErrorPage";
import HorizontalViewItem from "../../components/HorizontalItems/ViewItem";

// Функция для форматирования даты
const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month}.${year} в ${hours}:${minutes}`;
};

const {Title, Text} = Typography;

// Функция для отображения типа способа оплаты
const formatPaymentType = (type) => {
    const typeMap = {
        'cash': { label: 'Наличные', color: 'green' },
        'card': { label: 'Карта', color: 'blue' },
        'electronic': { label: 'Электронные', color: 'purple' },
        'other': { label: 'Прочие', color: 'default' }
    };

    const typeInfo = typeMap[type] || { label: type, color: 'default' };
    return <Tag color={typeInfo.color}>{typeInfo.label}</Tag>;
};

// Функция для отображения POS типа
const formatPosType = (posType) => {
    const posTypeMap = {
        'terminal': { label: 'Терминал', color: 'blue' },
        'mobile': { label: 'Мобильный', color: 'green' },
        'online': { label: 'Онлайн', color: 'purple' },
        'other': { label: 'Прочие', color: 'default' }
    };

    const posTypeInfo = posTypeMap[posType] || { label: posType, color: 'default' };
    return <Tag color={posTypeInfo.color}>{posTypeInfo.label}</Tag>;
};

const PaymentMethodPage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {message} = App.useApp();
    const {setPageTitle, setRightContent} = usePageTitle();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [formData, setFormData] = useState({
        type: [],
        pos_type: []
    });

    useEffect(() => {
        fetchData();
        setPageTitle('Просмотр способа оплаты');

        setRightContent(
            authService.hasPermission('pos:payment_methods:edit') && (
                <Button
                    type="primary"
                    onClick={() => navigate(`/payment-methods/${id}/edit`)}
                >
                    Редактировать
                </Button>
            )
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
            setPaymentMethod(null);
        };
    }, [setPageTitle, setRightContent, id, navigate]);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Получаем данные способа оплаты
            const paymentMethodResponse = await paymentMethodService.getPaymentMethodById(id);

            // Получаем данные для формы
            const formDataResponse = await paymentMethodService.getFormData();

            if (paymentMethodResponse.status === 'success' && formDataResponse.status === 'success') {
                setPaymentMethod(paymentMethodResponse.data);
                setFormData(formDataResponse.data);
                console.log('Получены данные способа оплаты:', paymentMethodResponse.data);
            } else {
                message.error('Не удалось загрузить данные способа оплаты');
                navigate('/payment-methods');
            }
        } catch (error) {
            if (error.response?.status === 404) {
                setError({
                    statusCode: 404,
                    title: "Способ оплаты не найден",
                    description: "Указанный способ оплаты не существует или был удален."
                });
            } else {
                setError({
                    statusCode: error.response?.status || 500,
                    title: "Ошибка загрузки данных",
                    description: "Произошла ошибка при загрузке данных способа оплаты. Попробуйте обновить страницу или вернуться позже."
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async () => {
        try {
            setHistoryLoading(true);
            const response = await paymentMethodService.getPaymentMethodHistory(id);

            if (response.status === 'success') {
                setHistoryData(response.data);
            } else {
                message.error('Не удалось загрузить историю изменений');
            }
        } catch (error) {
            message.error(error.message || 'Ошибка при загрузке истории изменений');
        } finally {
            setHistoryLoading(false);
            setHistoryLoaded(true);
        }
    };

    // Функция для получения лейбла из списка опций
    const getOptionLabel = (value, options) => {
        if (!value || !options) return 'Не указано';
        const option = options.find(opt => opt.value === value || opt.id === value);
        return option ? (option.label || option.name) : value;
    };

    if (loading) {
        return (
            <div style={{textAlign: 'center', padding: '50px 0'}}>
                <Spin size="large"/>
            </div>
        );
    }

    if (error) {
        return <ErrorPage
            statusCode={error.statusCode}
            title={error.title}
            description={error.description}
        />;
    }

    return (
        <Card>
            <Title level={3} style={{marginTop: 0}}>
                Способ оплаты «{paymentMethod.name}»
            </Title>
            <Space>
                <Text>ID: <Text>{paymentMethod.id}</Text></Text>
            </Space>

            <Tabs defaultActiveKey="data" items={[
                {
                    key: "data",
                    label: "Данные",
                    children: (
                        <Row gutter={24}>
                            <Col span={12}>
                                <Space direction="vertical" size={16} style={{ marginBottom: 38, display: 'block' }}>
                                    <Title level={5} style={{ marginBottom: 20 }}>Основные атрибуты</Title>
                                    <HorizontalViewItem label="Название">
                                        <Text>{paymentMethod.name || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Тип">
                                        {formatPaymentType(paymentMethod.type)}
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Внешний ID">
                                        <Text>{paymentMethod.external_id || 'Не указано'}</Text>
                                    </HorizontalViewItem>
                                </Space>

                                <Space direction="vertical" size={16} style={{ marginBottom: 38, display: 'block' }}>
                                    <Title level={5} style={{ marginBottom: 20 }}>Системная информация</Title>
                                    <HorizontalViewItem label="Дата создания">
                                        <Text>{formatDate(paymentMethod.created_at)}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Дата обновления">
                                        <Text>{formatDate(paymentMethod.updated_at)}</Text>
                                    </HorizontalViewItem>
                                </Space>
                            </Col>
                            <Col span={12}>
                                <Space direction="vertical" size={16} style={{ marginBottom: 38, display: 'block' }}>
                                    <Title level={5} style={{ marginBottom: 20 }}>POS настройки</Title>
                                    <HorizontalViewItem label="POS тип">
                                        {paymentMethod.pos_type ? formatPosType(paymentMethod.pos_type) : <Text>Не указано</Text>}
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="IP адрес POS">
                                        <Text>{paymentMethod.pos_ip_address || 'Не указано'}</Text>
                                    </HorizontalViewItem>
                                </Space>
                            </Col>
                        </Row>
                    )
                },
                {
                    key: "history",
                    label: "История изменений",
                    children: (
                        <Card>
                            {historyLoading ? (
                                <div style={{textAlign: 'center', padding: '50px 0'}}>
                                    <Spin size="large"/>
                                </div>
                            ) : (
                                <Steps
                                    direction="vertical"
                                    size="small"
                                    current={historyData.length}
                                    progressDot
                                    items={historyData.map(item => ({
                                        title: item.change_description,
                                        description: `${item.full_name} (${item.username}), ${formatDate(item.created_at)}`
                                    }))}
                                />
                            )}
                        </Card>
                    )
                }
            ]}
                  onChange={(key) => {
                      if (key === 'history' && !historyLoaded) {
                          fetchHistory();
                      }
                  }}/>
        </Card>
    );
}

export default PaymentMethodPage;